import { Product , SelectedProductForImport } from '../../stores/appReportsStore';
import { Platform } from '../../types/Platforms';
import { getAccessToken } from '../authentication/supabaseService';
import { FrontendRestaurantSectionPlatform } from '../restaurant/restaurantPlatformService';


export interface RawProductResponse {
  platform: string;
  category: string;
  name: string;
  brand: string;
  articleId: string;
  src: string;
  link: string;
  matchScore: number;
  price: number;
  isKiloPrice: boolean;
  unitPrice: number;
  unitPriceUnit: string;
  quantity: number;
  restaurantId?: string;
  restaurantName?: string;
  quantityInfo: {
    single: { standardUnit: string; standardizedAmount: number; estimatedWeightPerPiece: number };
    combo?: { standardizedAmount: number };
  };
  hasComboOption: boolean;
  comboPrice?: number;
  comboMultiplier?: number;
}

/**
 * Uploads a PDF file to /api/processFile and returns an array of
 * { articleId, amount } objects for initial textarea population.
 */
export async function processFile(
  file: File,
  platform: Platform
): Promise<{ list: { articleId: string; factor: number; amount: number; weight: number; }[], fileMetadata: { startDate: string, endDate: string, fileName: string }}> {
  const token = await getAccessToken();
  const form = new FormData();
  form.append('file', file);
  form.append('platform', platform)

  const res = await fetch('/api/processFile', {
    method: 'POST',
    body: form,
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to process file');
  }

  const data = await res.json();
  console.log("got raw file data: ", data);

  const fileMetadata = {
    ...data.metadata,
    fileName: file.name
  }
  return { 
    list: data.products as { articleId: string; factor: number; amount: number; weight: number;}[], 
    fileMetadata: fileMetadata as {startDate: string, endDate: string, fileName: string}};
}

/**
 * Sends a list of article IDs to /api/importProductIds, then maps
 * the raw response into our Product shape.
 */
export async function importByIds(
  ids: string[],
  platform: Platform,
  quantities: Record<string, { amount: number; factor: number; weight: number }>,
  availablePlatforms: FrontendRestaurantSectionPlatform[]
): Promise<Record<string, Product>> {
  const token = await getAccessToken();
  // get the correct restaurantplatform from list
  const restaurantPlatform = availablePlatforms.find(p => p.platformName === platform)
  if (!restaurantPlatform) {
    throw new Error('Restaurant platform not found')
  }
  const res = await fetch('/api/importProductIds', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ productIds: ids, platform: platform, quantities: quantities, restaurantPlatformId: restaurantPlatform.id }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to import products');
  }

  const { lists: raw }: { lists: Record<string, RawProductResponse> } =
    await res.json();

  console.log("[IMPORT - " + platform + "] Got raw products from server: ", raw)

  return Object.entries(raw).reduce<Record<string, Product>>((acc, [id, p]) => {
    const single = p.quantityInfo.single;
    acc[id] = {
      platform: Platform.fromString(p.platform),
      category: p.category,
      name: p.name,
      brand: p.brand,
      articleId: p.articleId,
      src: p.src,
      link: p.link,
      matchScore: 0,
      price: p.price,
      isKiloPrice: p.isKiloPrice,
      unitPrice: p.unitPrice,
      unitPriceUnit: p.unitPriceUnit,
      quantity: p.quantity,
      useSingle: true,
      useCombo: false,
      standardUnit: single.standardUnit,
      standardizedAmount: single.standardizedAmount,
      estimatedWeightPerPiece: single.estimatedWeightPerPiece,
      hasComboOption: p.hasComboOption,
      combo: p.hasComboOption
        ? {
            price: p.comboPrice!,
            multiplier: p.comboMultiplier!,
            standardizedAmount: p.quantityInfo.combo!.standardizedAmount,
          }
        : undefined,
    };
    return acc;
  }, {});
}

/**
 * Adds quantities from a mapping to each Product's quantity. 
 * If a productId has no entry in quantities, adds 1.
 */
export function addQuantitesToProducts(
    products: Record<string, Product>,
    quantities: Record<string, { amount: number; factor: number }>
  ): Record<string, Product> {
    const updatedProducts: Record<string, Product> = {};
  
    for (const [id, product] of Object.entries(products)) {
      const entry = quantities[id];
      if (entry.factor && entry.factor !== 1) {
        if (product.hasComboOption && product.combo?.multiplier === entry.factor) {
            updatedProducts[id] = {
                ...product,
                quantity: entry ? (entry.amount / entry.factor) : 1,
                useSingle: false,
                useCombo: true,
              };
        } else {
            updatedProducts[id] = {
                ...product,
                quantity: entry ? (entry.amount / entry.factor) : 1,
                useSingle: true,
                useCombo: false,
              };
        }
      } else {
        updatedProducts[id] = {
            ...product,
            quantity: entry ? entry.amount : 1,
            useSingle: true,
            useCombo: false,
        };
      }      
    }
  
    return updatedProducts;
}

export const calculateTotalSpent = (product: Product) => {
    if (product.useSingle) {
        return product.price * product.quantity
    } else if (product.useCombo) {
        const usePrice = product.combo?.price ?? product.price
        return usePrice * (product.quantity)
    } else {
        return product.price * product.quantity
    }
}


/**
 * Sends a list of article IDs to /api/importProductIds, then maps
 * the raw response into our Product shape.
 */
export async function importFromMultiplePlatformsByProductIds(
  importDtos: SelectedProductForImport[],
  availablePlatforms: FrontendRestaurantSectionPlatform[]
): Promise<Record<string, Product>> {
  const token = await getAccessToken();
  const platformlist = importDtos.map(p => p.platform)
  const platforms = Array.from(new Set(platformlist));
  // get the correct restaurantplatforms from list
  const restaurantPlatforms = platforms.map((platform) => {
    return availablePlatforms.find((p) => p.platformName === platform);
  }).filter((platform) => platform !== undefined);

  if (restaurantPlatforms.length !== platforms.length) {
    throw new Error('Some of the platforms are not set up in available restaurant platforms.')
  }

  const listOfRpIds = restaurantPlatforms.map((rp) => rp?.id)

  const res = await fetch('/api/importProductsCrossPlatforms', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ productDtos: importDtos, restaurantPlatformIds: listOfRpIds }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to import products');
  }

  const { lists: raw }: { lists: Record<string, RawProductResponse> } =
    await res.json();

  console.log("[IMPORT - Orderhistory] Got raw products from server: ", raw)

  return Object.entries(raw).reduce<Record<string, Product>>((acc, [id, p]) => {
    const single = p.quantityInfo.single;
    acc[id] = {
      platform: Platform.fromString(p.platform),
      category: p.category,
      name: p.name,
      brand: p.brand,
      articleId: p.articleId,
      src: p.src,
      link: p.link,
      matchScore: 0,
      price: p.price,
      isKiloPrice: p.isKiloPrice,
      unitPrice: p.unitPrice,
      unitPriceUnit: p.unitPriceUnit,
      quantity: p.quantity,
      useSingle: true,
      useCombo: false,
      standardUnit: single.standardUnit,
      standardizedAmount: single.standardizedAmount,
      estimatedWeightPerPiece: single.estimatedWeightPerPiece,
      hasComboOption: p.hasComboOption,
      combo: p.hasComboOption
        ? {
            price: p.comboPrice!,
            multiplier: p.comboMultiplier!,
            standardizedAmount: p.quantityInfo.combo!.standardizedAmount,
          }
        : undefined,
    };
    return acc;
  }, {});
}